/**
 * Utility functions for normalizing filter data between different formats.
 * Used primarily for filter state management and URL parameter handling.
 */

/**
 * Converts filter objects with array values to comma-separated strings.
 * Useful for storing filter state where string values are needed.
 *
 * @example
 * ```typescript
 * const input = { subjects: [1, 2, 3], category: 'news' };
 * const result = normalizeFiltersToStrings(input);
 * // Returns: { subjects: '1,2,3', category: 'news' }
 * ```
 *
 * @param filters - Object with string, number, or array values
 * @returns Object with all values converted to strings
 */
export function normalizeFiltersToStrings(
  filters: Record<string, string | number | (string | number)[]>
): Record<string, string> {
  return Object.entries(filters).reduce(
    (acc, [key, value]) => {
      acc[key] = Array.isArray(value) ? value.join(',') : String(value);
      return acc;
    },
    {} as Record<string, string>
  );
}

/**
 * Converts string values to single-element arrays.
 * Useful for preparing data in array format for API calls.
 *
 * @example
 * ```typescript
 * const input = { category: 'news', status: 'active' };
 * const result = normalizeStringsToArrays(input);
 * // Returns: { category: ['news'], status: ['active'] }
 * ```
 *
 * @param params - Object with string values
 * @returns Object with all values converted to single-element arrays
 */
export function normalizeStringsToArrays(params: Record<string, string>): Record<string, (string | number)[]> {
  return Object.entries(params).reduce(
    (acc, [key, val]) => {
      acc[key] = [val];
      return acc;
    },
    {} as Record<string, (string | number)[]>
  );
}

/**
 * Parses URL search parameters into an object with array values.
 * Handles duplicate keys by collecting them into arrays.
 *
 * @example
 * ```typescript
 * const queryString = '?subjects=1&subjects=2&category=news';
 * const result = parseUrlSearchParamsToArray(queryString);
 * // Returns: { subjects: ['1', '2'], category: ['news'] }
 * ```
 *
 * @param queryString - URL query string (with or without leading '?')
 * @returns Object with all values as arrays (even single values)
 */
export function parseUrlSearchParamsToArray(queryString: string): Record<string, string | string[]> {
  const params = new URLSearchParams(queryString);

  return [...params.entries()].reduce<Record<string, string | string[]>>((acc, tuple) => {
    const [key, val] = tuple;

    if (acc.hasOwnProperty(key)) {
      // Key already exists - convert to array or append to existing array
      if (Array.isArray(acc[key])) {
        acc[key] = [...acc[key], val];
      } else {
        acc[key] = [acc[key], val];
      }
    } else {
      // First occurrence - store as single-element array
      acc[key] = [val];
    }

    return acc;
  }, {});
}
